import { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { intersection } from 'lodash-es';
import type { EdgeProps } from 'reactflow';
import { produce } from 'immer';
import {
  BaseEdge,
  EdgeLabelRenderer,
  Position,
  getBezierPath,
  getSmoothStepPath,
  useStoreApi,
  useStore
} from 'reactflow';
import { RetweetOutlined, EditOutlined } from '@ant-design/icons'
import { useAvailableBlocks, useNodesInteractions } from './hooks';
import BlockSelector from './block-selector';
import BlockEditor from './block-editor'
import type { Edge, OnSelectBlock } from './types';
import { BlockEnum, NodeRunningStatus } from './types';
import { getEdgeColor } from './utils';
// import { ITERATION_CHILDREN_Z_INDEX, LOOP_CHILDREN_Z_INDEX } from './constants';
import CustomEdgeLinearGradientRender from './custom-edge-linear-gradient-render';
import cn from '@/utils/classnames';
import { ErrorHandleTypeEnum } from './nodes/_base/components/error-handle/types';
import { getEdgeParams } from './floatUtil';




const getPathByType = {
  'bezier': getBezierPath,
  'step': getSmoothStepPath
}
const CustomEdge = ({
  id,
  data,
  source,
  sourceHandleId,
  target,
  targetHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  selected,
  markerEnd,
  markerStart,
  style
}: EdgeProps) => {
  const currentType = data.type || 'bezier';
  const commonParams = {
    sourceX: sourceX - 8,
    sourceY,
    sourcePosition: Position.Right,
    targetX: targetX + 8,
    targetY,
    targetPosition: Position.Left,
    // curvature: 0.16,
  }

  if (data?.sourceType == BlockEnum.AssignmentOffline || data?.sourceType == BlockEnum.AssignmentOnline) {
    const sourceNode = useStore(useCallback((store) => store.nodeInternals.get(source), [source]));
    const targetNode = useStore(useCallback((store) => store.nodeInternals.get(target), [target]));

    if (!sourceNode || !targetNode) {
      return null;
    }


    const { sx, sy, sourcePos } = getEdgeParams(sourceNode, targetNode);

    commonParams.sourceX = sx;
    commonParams.sourceY = sy;
    commonParams.sourcePosition = sourcePos;


  }
  const meetPositon = [BlockEnum.AssignmentOffline, BlockEnum.AssignmentOnline, BlockEnum.Middle]
  if (data?.targetType && meetPositon.some(item => item === data?.targetType)) {
    const sourceNode = useStore(useCallback((store) => store.nodeInternals.get(source), [source]));
    const targetNode = useStore(useCallback((store) => store.nodeInternals.get(target), [target]));
    const { tx, ty, targetPos } = getEdgeParams(sourceNode, targetNode);
    commonParams.targetPosition = targetPos;
    commonParams.targetX = tx - (targetPos === Position.Left ? 3 : 0);
    commonParams.targetY = ty - (targetPos === Position.Top ? 2 : targetPos === Position.Bottom ? -1 : 0);
  }
  
  // 当currentType为bezier是参数加上curvature
  const param = currentType === 'bezier' ? { ...commonParams, curvature: 0.16 } : commonParams;
  
  const [edgePath, labelX, labelY] = getPathByType[currentType](param);
  const [open, setOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const store = useStoreApi();
  const { handleNodeAdd } = useNodesInteractions();
  const { availablePrevBlocks } = useAvailableBlocks(
    (data as Edge['data'])!.targetType,
    (data as Edge['data'])?.isInIteration,
    (data as Edge['data'])?.isInLoop,
  );
  const { availableNextBlocks } = useAvailableBlocks(
    (data as Edge['data'])!.sourceType,
    (data as Edge['data'])?.isInIteration,
    (data as Edge['data'])?.isInLoop,
  );
  const { _sourceRunningStatus, _targetRunningStatus } = data;

  const linearGradientId = useMemo(() => {
    if (
      (_sourceRunningStatus === NodeRunningStatus.Succeeded ||
        _sourceRunningStatus === NodeRunningStatus.Failed ||
        _sourceRunningStatus === NodeRunningStatus.Exception) &&
      (_targetRunningStatus === NodeRunningStatus.Succeeded ||
        _targetRunningStatus === NodeRunningStatus.Failed ||
        _targetRunningStatus === NodeRunningStatus.Exception ||
        _targetRunningStatus === NodeRunningStatus.Running)
    )
      return id;
  }, [_sourceRunningStatus, _targetRunningStatus, id]);

  const handleOpenChange = useCallback((v: boolean) => {
    setOpen(v);
  }, []);

  const handleEditOpenChange = useCallback((v: boolean) => {
    setEditorOpen(v)
  }, [])

  const handleInsert = useCallback<OnSelectBlock>(
    (nodeType, toolDefaultValue) => {
      handleNodeAdd(
        {
          nodeType,
          toolDefaultValue,
        },
        {
          prevNodeId: source,
          prevNodeSourceHandle: sourceHandleId || 'source',
          nextNodeId: target,
          nextNodeTargetHandle: targetHandleId || 'target',
        },
      );
    },
    [handleNodeAdd, source, sourceHandleId, target, targetHandleId],
  );

  const handleEditLable = useCallback((value) => {
    const { edges, setEdges } = store.getState();
    const newEdges = produce(edges, (draft) => {
      const currentEdge = draft.find(e => e.id === id)!
      // const type = currentEdge.data.type || 'bezier'
      currentEdge.data.label = value
      currentEdge.data._hovering = false
    });
    setEdges(newEdges);
    handleEditOpenChange(false)
  }, [store, id])

  const handleToggerLineType = useCallback(
    () => {
      const { edges, setEdges } = store.getState();
      const newEdges = produce(edges, (draft) => {
        const currentEdge = draft.find(e => e.id === id)!
        const type = currentEdge.data.type || 'bezier'
        currentEdge.data.type = type == 'bezier' ? 'step' : 'bezier'
      });
      setEdges(newEdges);
    },
    [store, id],
  );

  const stroke = useMemo(() => {
    if (selected) return getEdgeColor(NodeRunningStatus.Running);

    if (linearGradientId) return `url(#${linearGradientId})`;

    if (data?._connectedNodeIsHovering)
      return getEdgeColor(
        NodeRunningStatus.Running,
        sourceHandleId === ErrorHandleTypeEnum.failBranch,
      );

    return getEdgeColor();
  }, [
    data?._connectedNodeIsHovering,
    linearGradientId,
    selected,
    sourceHandleId,
  ]);
  // 边缘样式
  const edgeStyle = {
    ...style,
    strokeWidth: style?.strokeWidth || 2,
    stroke,
    opacity: data?._waitingRun ? 0.7 : 1,
  };
  return (
    <>
      {linearGradientId && (
        <CustomEdgeLinearGradientRender
          id={linearGradientId}
          startColor={getEdgeColor(_sourceRunningStatus)}
          stopColor={getEdgeColor(_targetRunningStatus)}
          position={{
            x1: sourceX,
            y1: sourceY,
            x2: targetX,
            y2: targetY,
          }}
        />
      )}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={edgeStyle}
         interactionWidth={20}
      />
      <EdgeLabelRenderer>
        <div 
          className={cn(
            " px-2 rounded nodrag nopan",
            selected ? "bg-blue-600 text-white" : "bg-gray-300",
            !(data?._hovering || editorOpen) ? "block" : "hidden",
          )}
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
            opacity: data?._waitingRun ? 0.7 : 1,
          }}
        >
          {data?.label}
        </div>
        <div
          className={cn(
            "nopan nodrag hover:scale-125",
            data?._hovering ? "block" : "hidden",
            (open || editorOpen) && "!block",
             
          )}
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
            opacity: data?._waitingRun ? 0.7 : 1,
          }}
        >
          <div className="flex items-center justify-center">
            <BlockSelector
              open={open}
              onOpenChange={handleOpenChange}
              asChild
              onSelect={handleInsert}
              availableBlocksTypes={intersection(
                availablePrevBlocks,
                availableNextBlocks
              )}
              triggerClassName={() => "hover:scale-150 transition-all"}
            />
            <div
              onClick={handleToggerLineType}
              title={currentType == 'bezier' ? '切换为直线' : '切换为曲线'}
              className="flex w-4 h-4 mx-1 justify-center rounded-full bg-components-button-primary-bg text-text-primary-on-surface cursor-pointer items-center hover:scale-150 transition-all"
            >
              <RetweetOutlined style={{ fontSize: '10px', fontWeight: 'bolder' }} className="h-2.5 w-2.5" />
            </div>
            <BlockEditor
              open={editorOpen}
              initVal={data?.label || ''}
              onOpenChange={handleEditOpenChange}
              asChild
              onOk={handleEditLable}
              triggerClassName={() => "hover:scale-150 transition-all"}
            />
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default memo(CustomEdge);
